import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, BarChart3 } from 'lucide-react';
import { apiFetch } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-red-500/10 blur-[140px] animate-float-slow" />
        </div>
        <div className="w-full max-w-md relative z-10">
          <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-red-400/30 via-red-400/10 to-transparent">
            <div className="rounded-3xl p-8 text-center backdrop-blur-xl" style={{
              background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(239,68,68,0.15)',
            }}>
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center" style={{
                background: 'rgba(239,68,68,0.12)',
                boxShadow: '0 0 40px rgba(239,68,68,0.25)',
              }}>
                <XCircle size={40} className="text-red-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-3">Invalid Link</h2>
              <p className="text-gray-400 text-sm mb-6">No reset token found. Please request a new password reset.</p>
              <Link to="/forgot-password" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors">
                Request new reset link →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(API_ROUTES.auth.resetPassword, {
        method: 'POST',
        body: { token, new_password: password },
        auth: false,
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Reset failed. The link may be expired.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
        </div>
        <div className="w-full max-w-md relative z-10 animate-scale-in">
          <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-emerald-400/10 to-transparent">
            <div className="rounded-3xl p-8 text-center backdrop-blur-xl" style={{
              background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(16,185,129,0.1)',
            }}>
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center animate-pulse-soft" style={{
                background: 'rgba(16,185,129,0.12)',
                boxShadow: '0 0 40px rgba(16,185,129,0.3)',
              }}>
                <CheckCircle size={40} className="text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-3">Password Reset!</h2>
              <p className="text-gray-400 text-sm mb-6">Your password has been updated. You can now login.</p>
              <Link to="/login" className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all" style={{
                background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                color: '#070912',
                boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
              }} onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
              }} onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
              }}>
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-500/5 blur-[150px]" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8 group">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
            <BarChart3 size={26} className="text-white" />
          </div>
          <h1 className="font-display text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">TrackMyFxEA</h1>
        </Link>

        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
          <div className="rounded-3xl p-8 backdrop-blur-xl" style={{
            background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            <div className="mb-6">
              <h2 className="font-display text-2xl font-bold text-white mb-1">Set new password</h2>
              <p className="text-sm text-gray-500">Enter your new password below</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">New Password</label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-12 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Confirm Password</label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 transition-colors" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>
                {confirmPassword && confirmPassword !== password && (
                  <p className="text-xs text-red-400 mt-1.5">Passwords don't match</p>
                )}
              </div>

              {error && (
                <div className="rounded-xl px-4 py-3 text-red-400 text-sm animate-fade-in" style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #14b8a6, #06b6d4)',
                  color: '#070912',
                  boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#070912] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock size={16} />
                    Reset Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="text-center mt-6">
          <Link to="/login" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
            ← Back to login
          </Link>
        </p>
      </div>
    </div>
  );
}
